import { useState } from "react";
import usePageMeta from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";

const FORM_URL = "https://readdy.ai/api/form/db6afgjghk82orf0cjs0";

const contactInfo = [
  {
    icon: "ri-mail-line",
    label: "商务邮箱",
    value: "brand@zhangjinyu.com",
    note: "合作洽谈 / 内容授权",
  },
  {
    icon: "ri-phone-line",
    label: "工作电话",
    value: "010-8888 6666",
    note: "工作日 10:00 - 18:00",
  },
  {
    icon: "ri-map-pin-line",
    label: "工作室地址",
    value: "北京市朝阳区创意园区 A 座",
    note: "来访请提前预约",
  },
];

const socials = [
  { icon: "ri-weibo-fill", label: "微博" },
  { icon: "ri-wechat-fill", label: "微信" },
  { icon: "ri-bilibili-fill", label: "哔哩哔哩" },
  { icon: "ri-youtube-fill", label: "视频号" },
];

interface FormState {
  name: string;
  email: string;
  message: string;
}

export default function Contact() {
  usePageMeta({
    title: "联系我们 - 张津瑜个人品牌官网",
    description:
      "通过联系页面与张津瑜个人品牌取得联系，提交合作咨询、内容授权或留言反馈。",
    keywords: "联系张津瑜,合作咨询,内容授权,联系方式",
  });

  const [form, setForm] = useState<FormState>({ name: "", email: "", message: "" });
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const validate = (): string => {
    if (!form.name.trim()) return "请填写您的姓名";
    if (!form.email.trim()) return "请填写您的邮箱";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return "请填写正确的邮箱格式";
    if (!form.message.trim()) return "请填写留言内容";
    if (form.message.length > 500) return "留言内容请控制在 500 字以内";
    return "";
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;

    const error = validate();
    if (error) {
      setStatus("error");
      setFormError(error);
      return;
    }

    const data = new FormData(formEl);
    const honeypot = (data.get("website_alt") as string | null)?.trim() ?? "";
    if (honeypot) {
      setStatus("success");
      return;
    }

    data.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setForm({ name: "", email: "", message: "" });
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交被拦截，请检查后重试。"
            : typeof serverMsg === "string" && serverMsg
              ? serverMsg
              : "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <>
      <section className="bg-primary-800 py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-10">
          <div className="[&_a]:text-background-300 [&_a:hover]:text-accent-400 [&_span]:text-background-100">
            <Breadcrumb items={[{ label: "联系我们" }]} />
          </div>
          <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">
            联系我们
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-300 md:text-base">
            无论是品牌联名、内容共创、公益合作，还是简单的留言交流，都欢迎通过以下方式与我们取得联系。
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-10">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[380px_1fr] lg:gap-14">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                联系方式
              </h2>
              <div className="mt-6 space-y-4">
                {contactInfo.map((info) => (
                  <div
                    key={info.label}
                    className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-4"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className={`${info.icon} text-xl`} />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">{info.label}</p>
                      <p className="mt-0.5 text-sm font-medium text-foreground-950">
                        {info.value}
                      </p>
                      <p className="mt-0.5 text-xs text-foreground-500">{info.note}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <h3 className="font-heading text-base font-bold text-foreground-950">
                  关注社交媒体
                </h3>
                <div className="mt-4 flex flex-wrap gap-3">
                  {socials.map((s) => (
                    <div
                      key={s.label}
                      className="flex h-16 w-16 flex-col items-center justify-center rounded-lg border border-background-200 bg-background-50 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                    >
                      <i className={`${s.icon} text-2xl`} />
                      <span className="mt-1 text-[10px]">{s.label}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-xs text-foreground-500">
                  扫码或搜索“张津瑜”即可关注官方账号（示意图）
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                留言给我们
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                填写以下信息，我们会在三个工作日内回复您。
              </p>

              <form
                className="mt-6 space-y-5"
                onSubmit={handleSubmit}
                data-readdy-form
                id="contact-form"
                noValidate
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="contact-name"
                      className="mb-1.5 block text-sm font-medium text-foreground-800"
                    >
                      您的姓名 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="请输入姓名"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact-email"
                      className="mb-1.5 block text-sm font-medium text-foreground-800"
                    >
                      联系邮箱 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="请输入邮箱"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="contact-message"
                    className="mb-1.5 block text-sm font-medium text-foreground-800"
                  >
                    留言内容 <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="请简单描述您的合作意向、问题或建议（最多 500 字）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                  <div className="mt-1 text-right text-xs text-foreground-500">
                    {form.message.length} / 500
                  </div>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="form-guard-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "success" && (
                  <div className="flex items-start gap-2.5 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-700">
                    <i className="ri-checkbox-circle-line mt-0.5 text-lg" />
                    <span>提交成功！感谢您的留言，我们会尽快与您联系。</span>
                  </div>
                )}
                {status === "error" && formError && (
                  <div className="flex items-start gap-2.5 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-accent-800">
                    <i className="ri-error-warning-line mt-0.5 text-lg" />
                    <span>{formError}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base" />
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill text-base" />
                      提交留言
                    </>
                  )}
                </button>
                <p className="text-xs text-foreground-500">
                  提示：本站为前端演示站点，表单内容将提交至内置表单服务用于收集咨询。
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}